import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'hs-lodash/isEqual';
import omit from 'transmute/omit';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import useCurrentView from 'framework-listing-lib/hooks/useCurrentView';
import { updateHiddenFilterForCalendarDisplayDate } from 'framework-listing-lib/internal/Calendar/utils/filters';
import { updateHiddenFilterForFolder } from 'framework-listing-lib/internal/Folders/utils/filters';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
import { updateFilterGroupsForBUNav, updateHiddenFilterForBUNav, updateQuickFiltersForBUNav } from 'framework-listing-lib/internal/businessUnits/utils';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useDeepEqual from 'framework-listing-lib/internal/hooks/useDeepEqual';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { areSameFilterGroups, combineFilterStates, combineFilters, getAggregatedFilterGroups, getInitialFiltersStateForUIMode, makeFiltersState, removeFilterGroups } from 'framework-listing-lib/utils/filters';
import useSyncFiltersWithSessionStorage from './useSyncFiltersWithSessionStorage';
import { useCurrentUIMode } from './useUIMode';

/**
 * This hook is meant to be used only by CrmObjectTypeListingClientProvider.
 * It initialises the global filter state used by all hooks & components in this codebase.
 *
 * We update filters from this hook in two scenarios:
 *  - when @initialFilter or @objectType prop changes.
 *  - when the current view changes.
 *
 * This hooks has & controls:
 *  - list of deleted object ids (due to the delay with the CRM, we manually omit deleted objects when sending request to crm-search).
 *  - filters that have been applied to the UI
 */
export default function useCrmObjectTypeListingFilterState(t0) {
  "use memo";

  const $ = _c(74);
  const {
    calendarProps,
    hiddenFilter: hiddenFilterProp,
    initialFilter: initialFilterProp,
    folderId
  } = t0;
  const urlParams = useHandleQueryParams();
  const currentUIMode = useCurrentUIMode();
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const initialFilter = useDeepEqual(initialFilterProp);
  const hasBUNavIntegration = useHasBUNavIntegration();
  const objectType = useObjectTypeId();
  const initialFilterRef = useRef(initialFilter);
  const previousUIMode = useRef(currentUIMode);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [deletedObjects, onSetDeletedObjects] = useState(t1);
  const [filterStateSource, setFilterStateSource] = useState("other");
  let t2;
  if ($[1] !== currentUIMode || $[2] !== currentView || $[3] !== hasViews || $[4] !== initialFilter) {
    t2 = () => {
      if (!currentView || !hasViews) {
        return getInitialFiltersStateForUIMode({
          initialFilter,
          uiMode: currentUIMode
        });
      }
      return makeFiltersState({
        initialFilter,
        currentView,
        uiMode: currentUIMode
      });
    };
    $[1] = currentUIMode;
    $[2] = currentView;
    $[3] = hasViews;
    $[4] = initialFilter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [filters, setFiltersState] = useState(t2);
  const {
    filterStateFromSessionStorage,
    turnOnSessionStorage
  } = useSyncFiltersWithSessionStorage(filters);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = stateUpdate => {
      setFiltersState(stateUpdate);
      setFilterStateSource("other");
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const internalOnSetFiltersState = t3;
  let t4;
  if ($[7] !== currentUIMode || $[8] !== initialFilter) {
    t4 = () => {
      const hasInitialFilter = Boolean(initialFilterRef.current && initialFilter);
      const nonQueryFiltersAreEqual = isEqual(omit(["query"], initialFilterRef.current || {}), omit(["query"], initialFilter || {}));
      const queryIsDifferent = Boolean(initialFilterRef.current && initialFilter && initialFilterRef.current.query !== initialFilter.query);
      const uiModeIsDifferent = currentUIMode !== previousUIMode.current;
      const hasOnlyQueryChanged = hasInitialFilter && nonQueryFiltersAreEqual && queryIsDifferent;
      if (hasOnlyQueryChanged) {
        internalOnSetFiltersState(prevFilter => Object.assign({}, prevFilter, {
          query: (initialFilter === null || initialFilter === void 0 ? void 0 : initialFilter.query) || ""
        }));
      } else {
        if (!nonQueryFiltersAreEqual || uiModeIsDifferent) {
          internalOnSetFiltersState(prevFilter_0 => {
            let newFilter;
            if (previousUIMode.current !== UI_MODE.FOLDERS && currentUIMode !== UI_MODE.FOLDERS) {
              newFilter = combineFilterStates({
                initialFilter,
                newFilter: prevFilter_0,
                uiMode: currentUIMode
              });
            } else {
              newFilter = getInitialFiltersStateForUIMode({
                initialFilter,
                uiMode: currentUIMode
              });
            }
            previousUIMode.current = currentUIMode;
            return isEqual(prevFilter_0, newFilter) ? prevFilter_0 : newFilter;
          });
        }
      }
      initialFilterRef.current = initialFilter;
    };
    $[7] = currentUIMode;
    $[8] = initialFilter;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== currentUIMode || $[11] !== initialFilter || $[12] !== objectType) {
    t5 = [currentUIMode, initialFilter, internalOnSetFiltersState, objectType];
    $[10] = currentUIMode;
    $[11] = initialFilter;
    $[12] = objectType;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  const [currentViewId, setCurrentViewId] = useState(undefined);
  let t6;
  if ($[14] !== currentUIMode || $[15] !== currentView || $[16] !== currentViewId || $[17] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) || $[18] !== (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) || $[19] !== initialFilter) {
    t6 = () => {
      if (currentUIMode === UI_MODE.FOLDERS || !currentView) {
        setCurrentViewId(undefined);
        return;
      }
      const viewId = getViewId(currentView);
      if (currentViewId === viewId) {
        return;
      }
      setCurrentViewId(viewId);
      internalOnSetFiltersState(prevFilter_1 => {
        const newFilter_0 = makeFiltersState({
          initialFilter,
          currentView: Object.assign({}, currentView, {
            filterGroups: (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups) || currentView.filterGroups,
            quickFilters: (filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters) || currentView.quickFilters
          }),
          uiMode: currentUIMode
        });
        return isEqual(prevFilter_1, newFilter_0) ? prevFilter_1 : newFilter_0;
      });
    };
    $[14] = currentUIMode;
    $[15] = currentView;
    $[16] = currentViewId;
    $[17] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
    $[18] = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
    $[19] = initialFilter;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const t7 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.filterGroups;
  const t8 = filterStateFromSessionStorage === null || filterStateFromSessionStorage === void 0 ? void 0 : filterStateFromSessionStorage.quickFilters;
  let t9;
  if ($[21] !== currentUIMode || $[22] !== currentView || $[23] !== currentViewId || $[24] !== initialFilter || $[25] !== t7 || $[26] !== t8) {
    t9 = [currentUIMode, currentView, currentViewId, t7, t8, initialFilter, internalOnSetFiltersState];
    $[21] = currentUIMode;
    $[22] = currentView;
    $[23] = currentViewId;
    $[24] = initialFilter;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  useEffect(t6, t9);
  const [hasUsedQueryParams, setHasUsedQueryParams] = useState(false);
  let t10;
  let t11;
  if ($[28] !== currentUIMode || $[29] !== currentView || $[30] !== hasUsedQueryParams || $[31] !== hasViews || $[32] !== initialFilter || $[33] !== urlParams) {
    t10 = () => {
      const isLoadingOrInvalidView = hasViews && !currentView;
      if (isLoadingOrInvalidView || !urlParams || hasUsedQueryParams) {
        return;
      }
      internalOnSetFiltersState(prevFilter_2 => {
        const newFilter_1 = currentView ? makeFiltersState({
          initialFilter,
          currentView: Object.assign({}, currentView, {
            filterGroups: combineFilters(currentView.filterGroups, urlParams.filterGroups),
            filters: [...currentView.filters, ...urlParams.filters],
            quickFilters: [...currentView.quickFilters, ...urlParams.quickFilters]
          }),
          uiMode: currentUIMode
        }) : Object.assign({}, prevFilter_2, {
          filterGroups: combineFilters(prevFilter_2.filterGroups, urlParams.filterGroups),
          quickFilters: [...prevFilter_2.quickFilters, ...urlParams.quickFilters]
        });
        return isEqual(prevFilter_2, newFilter_1) ? prevFilter_2 : newFilter_1;
      });
      setHasUsedQueryParams(true);
    };
    t11 = [currentUIMode, currentView, hasUsedQueryParams, hasViews, initialFilter, internalOnSetFiltersState, urlParams];
    $[28] = currentUIMode;
    $[29] = currentView;
    $[30] = hasUsedQueryParams;
    $[31] = hasViews;
    $[32] = initialFilter;
    $[33] = urlParams;
    $[34] = t10;
    $[35] = t11;
  } else {
    t10 = $[34];
    t11 = $[35];
  }
  useEffect(t10, t11);
  let t12;
  if ($[36] !== turnOnSessionStorage) {
    t12 = (stateUpdate_0, options) => {
      turnOnSessionStorage();
      setFilterStateSource((options === null || options === void 0 ? void 0 : options.source) || "other");
      setFiltersState(stateUpdate_0);
    };
    $[36] = turnOnSessionStorage;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const externalOnSetFilters = t12;
  let t13;
  if ($[38] !== filters.filterGroups || $[39] !== filters.quickFilters) {
    t13 = propertyName => filters.quickFilters.some(filterOperator_0 => filterOperator_0.property === propertyName) || filters.filterGroups.some(filterGroup => filterGroup.filters.some(filterOperator => filterOperator.property === propertyName));
    $[38] = filters.filterGroups;
    $[39] = filters.quickFilters;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const hasPropertyFilter = t13;
  hiddenFilterProp === null || hiddenFilterProp === void 0 || hiddenFilterProp.filterGroups;
  let t14;
  if ($[41] !== filters.filterGroups || $[42] !== hasBUNavIntegration || $[43] !== (hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups)) {
    const filterGroupsWithoutHiddenFilter = removeFilterGroups(filters.filterGroups, (hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups) || []);
    t14 = updateFilterGroupsForBUNav({
      filterGroups: filterGroupsWithoutHiddenFilter,
      hasBUNavIntegration
    });
    $[41] = filters.filterGroups;
    $[42] = hasBUNavIntegration;
    $[43] = hiddenFilterProp === null || hiddenFilterProp === void 0 ? void 0 : hiddenFilterProp.filterGroups;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const filterGroupsWithBUNav = t14;
  const filterGroups = filterGroupsWithBUNav;
  let t15;
  if ($[45] !== filters.quickFilters || $[46] !== hasBUNavIntegration) {
    t15 = updateQuickFiltersForBUNav({
      quickFilters: filters.quickFilters,
      hasBUNavIntegration
    });
    $[45] = filters.quickFilters;
    $[46] = hasBUNavIntegration;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const quickFilters = t15;
  let t16;
  if ($[48] !== calendarProps || $[49] !== currentUIMode || $[50] !== folderId || $[51] !== hasBUNavIntegration || $[52] !== hiddenFilterProp) {
    bb0: {
      const withHiddenBUFilter = updateHiddenFilterForBUNav({
        hiddenFilter: hiddenFilterProp,
        hasBUNavIntegration
      });
      const withHiddenFolderFilter = updateHiddenFilterForFolder({
        folderId,
        hiddenFilter: withHiddenBUFilter,
        isFoldersMode: currentUIMode === UI_MODE.FOLDERS
      });
      if (!calendarProps || currentUIMode !== UI_MODE.CALENDAR) {
        t16 = withHiddenFolderFilter;
        break bb0;
      }
      t16 = updateHiddenFilterForCalendarDisplayDate({
        displayTimestamp: calendarProps.displayTimestamp,
        endPropertyName: calendarProps.endPropertyName,
        hiddenFilter: withHiddenFolderFilter,
        startPropertyName: calendarProps.startPropertyName,
        viewType: calendarProps.use
      });
    }
    $[48] = calendarProps;
    $[49] = currentUIMode;
    $[50] = folderId;
    $[51] = hasBUNavIntegration;
    $[52] = hiddenFilterProp;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const hiddenFilter = t16;
  let t17;
  bb1: {
    if (!currentView) {
      t17 = false;
      break bb1;
    }
    let t18;
    if ($[54] !== currentView.filterGroups || $[55] !== currentView.quickFilters || $[56] !== deletedObjects || $[57] !== filterGroups || $[58] !== hasBUNavIntegration || $[59] !== hiddenFilter || $[60] !== quickFilters) {
      const now = Date.now();
      const getNow = () => now;
      const currentAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups,
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters,
        getNow
      });
      const viewAggregatedFilterGroups = getAggregatedFilterGroups({
        deletedObjects,
        filterGroups: updateFilterGroupsForBUNav({
          filterGroups: currentView.filterGroups,
          hasBUNavIntegration
        }),
        hiddenFilter,
        includeHiddenFilter: true,
        quickFilters: updateQuickFiltersForBUNav({
          quickFilters: currentView.quickFilters,
          hasBUNavIntegration
        }),
        getNow
      });
      t18 = areSameFilterGroups(currentAggregatedFilterGroups, viewAggregatedFilterGroups);
      $[54] = currentView.filterGroups;
      $[55] = currentView.quickFilters;
      $[56] = deletedObjects;
      $[57] = filterGroups;
      $[58] = hasBUNavIntegration;
      $[59] = hiddenFilter;
      $[60] = quickFilters;
      $[61] = t18;
    } else {
      t18 = $[61];
    }
    t17 = t18;
  }
  const isFilterFromCustomView = t17;
  let t18;
  if ($[62] !== filterGroups || $[63] !== filters || $[64] !== quickFilters) {
    t18 = Object.assign({}, filters, {
      filterGroups,
      quickFilters
    });
    $[62] = filterGroups;
    $[63] = filters;
    $[64] = quickFilters;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== deletedObjects || $[67] !== externalOnSetFilters || $[68] !== filterStateSource || $[69] !== hasPropertyFilter || $[70] !== hiddenFilter || $[71] !== isFilterFromCustomView || $[72] !== t18) {
    t19 = {
      deletedObjects,
      filters: t18,
      filterStateSource,
      hasPropertyFilter,
      hiddenFilter,
      isFilterFromCustomView,
      onSetDeletedObjects,
      onSetFilters: externalOnSetFilters
    };
    $[66] = deletedObjects;
    $[67] = externalOnSetFilters;
    $[68] = filterStateSource;
    $[69] = hasPropertyFilter;
    $[70] = hiddenFilter;
    $[71] = isFilterFromCustomView;
    $[72] = t18;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  const value = t19;
  return value;
}